import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  GET_GRAPH_FOLDER,
  GET_GRAPH_FOLDERS,
  CREATE_GRAPH_FOLDER,
  UPDATE_GRAPH_FOLDER,
  REMOVE_GRAPH_FOLDER,
  MANAGE_GRAPH_FOLDER,
  SEARCH_GRAPH_FOLDERS,
  GRAPH_FOLDER_ACCESS,
  GET_GRAPH_FOLDER_LAYERS,
  SET_GRAPH_FOLDERS_REQ_STATUS,
} from '@control-front-end/common/constants/graphFolders';
import { MANAGE_FOLDER_LAYERS } from '@control-front-end/common/constants/graphLayers';
import AppUtils from '@control-front-end/utils/utils';
import { RequestStatus } from 'constants';
import api from '@control-front-end/common/sagas/api';
import { updateAutoLayerEls } from './graphHelpers';

/**
 * Создать модель графа
 */
export function* makeFolderModel(models) {
  const config = yield select((state) => state.config);
  const auth = yield select((state) => state.auth);
  models = Array.isArray(models) ? models : [models];
  for (const model of models) {
    model.ownerAvatar = config.avatarPath.replace('{userId}', model.ownerId);
    if (!model.access) {
      model.access = [
        {
          name: auth.nick,
          privs: model.privs,
          userId: auth.id,
          userType: 'user',
        },
      ];
    }
    for (const a of model.access) {
      a.avatar = config.avatarPath.replace('{userId}', a.userId);
    }
    model.pictureUrl = model.picture
      ? AppUtils.makeAppUrl(`/download/${model.picture}`)
      : null;
  }
}

/**
 * Изменить права доступа на граф
 */
function* manageGraphFolderAccess({ payload }) {
  const { graphFolderId, access } = payload;
  const { list } = yield select((state) => state.graphFolders);
  const newList = list.slice();
  const index = newList.findIndex((i) => i.id === graphFolderId);
  newList.splice(index, 1, { ...list[index], access });
  yield put({
    type: GRAPH_FOLDER_ACCESS.SUCCESS,
    payload: { list: newList },
  });
  yield updateAutoLayerEls({
    layerId: 'graphs',
    id: graphFolderId,
    status: 'updated',
    data: { access },
  });
}

/**
 * Создать граф
 */
function* createGraphFolder({ payload, callback }) {
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/graph_folders/${accId}`,
    body: payload,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const { list } = yield select((state) => state.graphFolders);
  const newList = list.slice();
  const newFolder = data.data;
  yield call(makeFolderModel, newFolder);
  newList.unshift(newFolder);
  yield put({
    type: CREATE_GRAPH_FOLDER.SUCCESS,
    payload: { list: newList },
  });
  if (callback) callback(data.data);
}

/**
 * Обновить граф
 */
function* updateGraphFolder({ payload, callback }) {
  const { id, name } = payload;
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/graph_folders/${id}`,
    body: { name },
  });
  if (result !== RequestStatus.SUCCESS) return;
  const { list } = yield select((state) => state.graphFolders);
  const copyList = list.slice();
  const index = copyList.findIndex((i) => i.id === id);
  const updatedFolder = data.data;
  yield call(makeFolderModel, updatedFolder);
  copyList.splice(index, 1, { ...copyList[index], ...updatedFolder });
  yield put({
    type: UPDATE_GRAPH_FOLDER.SUCCESS,
    payload: { list: copyList },
  });
  yield updateAutoLayerEls({
    layerId: 'graphs',
    id,
    status: 'updated',
    data: { title: name, name },
  });
  if (callback) callback(data.data);
}

/**
 * Добавить/удалить слои с графа
 */
export function* manageGraphFolder({ payload, callback }) {
  const { id, actions, manageLayers = true } = payload;
  const body = actions.map(({ action, id: layerId }) => ({
    action,
    data: { id: layerId, type: 'layer' },
  }));
  const { result } = yield call(api, {
    method: 'post',
    url: `/graph_folders/layers/${id}`,
    body,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const { list: graphFoldersList } = yield select(
    (state) => state.graphFolders
  );
  const list = structuredClone(graphFoldersList);
  const graphFolder = list.find((i) => i.id === id);
  if (graphFolder) {
    const groupActions = AppUtils.groupBy(body, 'action');
    graphFolder.layersCount += groupActions.create
      ? groupActions.create.length
      : 0;
    graphFolder.layersCount -= groupActions.delete
      ? groupActions.delete.length
      : 0;
    yield put({ type: MANAGE_GRAPH_FOLDER.SUCCESS, payload: { list } });
  }
  if (manageLayers) {
    yield put({
      type: MANAGE_FOLDER_LAYERS.REQUEST,
      payload: { graphFolderId: id, actions },
    });
  }
  if (callback) callback();
}

/**
 * Удалить граф
 */
function* removeGraphFolder({ payload: id, callback }) {
  const { result } = yield call(api, {
    method: 'delete',
    url: `/graph_folders/${id}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const { list } = yield select((state) => state.graphFolders);
  const newList = list.filter((i) => i.id !== id);
  yield put({
    type: REMOVE_GRAPH_FOLDER.SUCCESS,
    payload: { list: newList },
  });
  yield updateAutoLayerEls({
    layerId: 'graphs',
    id,
    status: 'removed',
  });
  if (callback) callback();
}

/**
 * Получить список доступных графов
 */
function* getGraphFolders({ payload, callback }) {
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const graphFoldersState = yield select((state) => state.graphFolders);
  const {
    reqStatus,
    list,
    limit: defaultLimit,
    offset,
    endList,
  } = graphFoldersState;
  const { loadMore, limit: customLimit } = payload;
  const limit = customLimit || defaultLimit;
  if (reqStatus !== RequestStatus.SUCCESS || endList) return;
  // Ставим флаг прогресса загрузки
  yield put({ type: SET_GRAPH_FOLDERS_REQ_STATUS, payload: 'inProgress' });
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/graph_folders/list/${accId}`,
    queryParams: { limit, offset },
  });
  // Ставим флаг окончания загрузки
  yield put({ type: SET_GRAPH_FOLDERS_REQ_STATUS, payload: 'success' });
  if (result !== RequestStatus.SUCCESS) return;
  const copyList = list.slice();
  const resList = data.data.list.slice();
  yield call(makeFolderModel, resList);
  const newList = loadMore ? copyList.concat(resList) : resList;
  yield put({
    type: GET_GRAPH_FOLDERS.SUCCESS,
    payload: {
      total: data.data.total,
      list: newList,
      limit,
      offset: offset + limit,
      endList: !data.data.list.length,
    },
  });
  if (callback) callback(newList);
}

/**
 * Получить список доступных графов
 */
function* searchGraphFolders({ payload, callback }) {
  const { query } = payload;
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/graph_folders/search/${accId}/${encodeURIComponent(query)}`,
  });
  // Ставим флаг окончания загрузки
  yield put({ type: SET_GRAPH_FOLDERS_REQ_STATUS, payload: 'success' });
  if (result !== RequestStatus.SUCCESS) return;
  const list = data.data;
  yield call(makeFolderModel, list);
  yield put({
    type: SEARCH_GRAPH_FOLDERS.SUCCESS,
    payload: { list },
  });
  if (callback) callback(list);
}

/**
 * Получить граф
 */
export function* getGraphFolder({
  payload: graphFolderId,
  callback,
  errorCallback,
}) {
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/graph_folders/${graphFolderId}`,
    handleErrCodes: [403],
  });
  if (data.statusCode === 403 && errorCallback) {
    errorCallback();
  }
  if (result !== RequestStatus.SUCCESS) return [];
  const graphFolder = { ...data.data };
  yield makeFolderModel(graphFolder);
  yield put({
    type: GET_GRAPH_FOLDERS.SUCCESS,
    payload: { list: [graphFolder] },
  });
  if (callback) callback(graphFolder);
  return graphFolder;
}

/**
 * Получить список доступных слоев графа
 */
export function* getGraphFolderLayers({ payload, callback }) {
  const {
    graphFolderId,
    list,
    limit = 10,
    offset = 0,
    pinned,
    loadMore,
  } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/graph_folders/layers/${graphFolderId}`,
    queryParams: { limit, offset, pinned },
  });
  if (result !== RequestStatus.SUCCESS) return [];
  if (callback) {
    const newList = loadMore ? list.concat(data.data) : data.data;
    callback({
      list: newList,
      offset: offset + limit,
      endList: !data.data.length,
    });
  }
  data.data.forEach((i) => {
    i.graphFolderId = graphFolderId;
  });
  return data.data;
}

function* graphFolders() {
  yield takeEvery(GET_GRAPH_FOLDER.REQUEST, getGraphFolder);
  yield takeEvery(GET_GRAPH_FOLDERS.REQUEST, getGraphFolders);
  yield takeEvery(SEARCH_GRAPH_FOLDERS.REQUEST, searchGraphFolders);
  yield takeEvery(CREATE_GRAPH_FOLDER.REQUEST, createGraphFolder);
  yield takeEvery(UPDATE_GRAPH_FOLDER.REQUEST, updateGraphFolder);
  yield takeEvery(MANAGE_GRAPH_FOLDER.REQUEST, manageGraphFolder);
  yield takeEvery(REMOVE_GRAPH_FOLDER.REQUEST, removeGraphFolder);
  yield takeLatest(GRAPH_FOLDER_ACCESS.REQUEST, manageGraphFolderAccess);
  yield takeLatest(GET_GRAPH_FOLDER_LAYERS.REQUEST, getGraphFolderLayers);
}

export default graphFolders;
